// @flow

// Types
import type {
  BillingInfoOperation,
} from '../../types'
import type {
  User,
} from 'fc-model-users/types'

import type {
  BillingInfo,
} from 'hfty-model-billing-info'

// Operator
import BillingInfoOperator from '../operator'

// Effect Managers
import { call, fork, put, race, select, take } from 'redux-saga/effects'

// Controllers
import controller from 'fc-ctrl-users'

// Selectors
import { billingInfoOperation$ as operations$ } from 'hfty-users-dom'
import { authenticatedUser$ } from 'fc-users'
import { currentUser$ } from 'hfty-users'

import { getToken } from 'fc-auth'
import url from 'hfty-utils/url'

const operator = BillingInfoOperator

type OperationsArgs = {
  operations$: (state: Object) => Array<BillingInfoOperation>
}

// Exports
export default function *watchOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  while (true) {
    yield take(BillingInfoOperator.actionTypes.edit())
    yield fork(handleOperations, {operations$})
  }
}

export function *handleOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  let done = false
  while (!done) {
    const effect = yield race({
      submit: take(operator.actionTypes.submitOperations()),
      cancel: take(operator.actionTypes.cancelOperations()),
    })
    if (effect.submit) {
      done = yield call(handleSubmitOperations, {operations$})
    } else {
      done = true // Cancelled
    }
  }
}

export function *handleSubmitOperations (): Generator<*, boolean, *> {
  let operations = yield select(operations$)

  operations = {
    ...operations,
    submitting: true,
  }

  const nextOperations = yield call(handleSubmitOperation, operations)
  return nextOperations.success
}

export function *handleSubmitOperation (operation: BillingInfoOperation): Generator<*, BillingInfoOperation, *> {
  const model: BillingInfo = operation.model
  const clientBillingId: number = model.id
  const parentUserId: number = yield select(authenticatedUser$)
  const user: User = yield select(currentUser$)

  const token = yield call(getToken)
  const response = yield call(fetch, url({
    path: 'hefty/clientbillingaddress/',
    id: clientBillingId,
  }), {
    method: clientBillingId ? 'PUT' : 'POST',
    body: JSON.stringify(model),
    headers: {
      Authorization: `Token ${token}`,
      'Content-Type': 'application/json',
    },
  })

  const error = response.status < 200 || response.status >= 300

  const json = yield call([response, response.json])

  if (error) {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: false,
      errors: json,
    }

    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)

    return nextOperation
  } else {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: true,
    }

    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)

    yield call(controller.read, {
      url: '/hefty/clientbillingaddress/',
      queryParams: {
        id: clientBillingId,
      },
    })

    yield put(controller.actionCreators.receive({
      id: parentUserId,
      data: {
        [parentUserId]: {
          ...user,
          clientBillingAddress: {
            ...model,
          },
        },
      },
    }))

    yield put(operator.actionCreators.cancelOperation(nextOperation))

    return nextOperation
  }
}
